import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  setLoadingForType,
  setResultsForType,
  setErrorForType,
} from '../../store/searchSlice';
import { similarBrandSearchSuccess, similarBrandSearchError } from '../../store/brandNameSlice';
import { similarAnalogSearchSuccess, similarAnalogSearchError } from '../../store/analogGeneratorSlice';
import { globalSearchSubtaskUpdate } from '../../store/globalSearchSlice';
import { isSearchRequestType } from '../../shared/searchRequestTypes';
import { extractSearchResultList, extractYandexImageSearchPayload } from '../../utils/socketPayloads';

export function useSubtaskUpdates(socket) {
  const dispatch = useDispatch();

  const handleSubtaskUpdate = useCallback(
    (update) => {
      if (!update || typeof update !== 'object') return;
      const taskInfo = update.taskInfo ?? update.task_info;
      const result =
        update.result ??
        update.data ??
        update.payload?.result ??
        update.data?.result;

      if (!taskInfo?.request_type) return;

      if (taskInfo.request_type === 'yandexImageSearch') {
        const taskId = taskInfo.task_id;
        if (!taskId) return;

        const pipelineStage = taskInfo.pipeline_stage ?? taskInfo.pipelineStage ?? null;
        const status = taskInfo.status;

        if (status === 'error' || pipelineStage === 'error') {
          const errMsg =
            (typeof result === 'string' && result) ||
            (result && typeof result === 'object' && result.message) ||
            update?.message ||
            'Ошибка при глобальном поиске по изображению';
          dispatch(
            globalSearchSubtaskUpdate({
              taskId,
              error: true,
              message: typeof errMsg === 'string' ? errMsg : 'Ошибка при глобальном поиске по изображению',
            })
          );
          return;
        }

        const partial = extractYandexImageSearchPayload(result);
        dispatch(
          globalSearchSubtaskUpdate({
            taskId,
            pipelineStage,
            status,
            partial: partial || undefined,
          })
        );
        return;
      }

      if (taskInfo.request_type === 'similar_brand') {
        const suggestionId = taskInfo.description?.suggestionId;
        if (!suggestionId) return;
        if (taskInfo.status === 'completed' || taskInfo.status === 'success') {
          dispatch(similarBrandSearchSuccess({ suggestionId, data: extractSearchResultList(result) || [] }));
        } else if (taskInfo.status === 'error') {
          dispatch(similarBrandSearchError({ suggestionId, error: 'Ошибка при поиске похожих названий' }));
        }
        return;
      }

      if (taskInfo.request_type === 'similar_analog') {
        const suggestionId = taskInfo.description?.suggestionId;
        if (!suggestionId) return;
        if (taskInfo.status === 'completed' || taskInfo.status === 'success') {
          dispatch(similarAnalogSearchSuccess({ suggestionId, data: extractSearchResultList(result) || [] }));
        } else if (taskInfo.status === 'error') {
          dispatch(similarAnalogSearchError({ suggestionId, error: 'Ошибка при поиске похожих аналогов' }));
        }
        return;
      }

      const requestType = taskInfo.request_type;
      if (!isSearchRequestType(requestType)) return;

      if (taskInfo.status === 'completed' || taskInfo.status === 'success') {
        dispatch(setLoadingForType({ requestType, value: false }));
        dispatch(setErrorForType({ requestType, error: null }));
        const list = extractSearchResultList(result);
        dispatch(setResultsForType({ requestType, result: list?.length ? list : null }));
      } else if (taskInfo.status === 'error') {
        dispatch(setLoadingForType({ requestType, value: false }));
        dispatch(setErrorForType({ requestType, error: `Произошла ошибка при поиске ${requestType}` }));
      } else {
        dispatch(setLoadingForType({ requestType, value: true }));
        dispatch(setErrorForType({ requestType, error: null }));
      }
    },
    [dispatch]
  );

  const handleTaskStatusUpdate = useCallback(
    (payload) => {
      const taskInfo = payload?.taskInfo ?? payload?.task_info;
      if (!taskInfo || taskInfo.request_type !== 'yandexImageSearch') return;
      const taskId = taskInfo.task_id ?? payload?.taskId;
      if (!taskId) return;
      dispatch(
        globalSearchSubtaskUpdate({
          taskId,
          pipelineStage: taskInfo.pipeline_stage ?? taskInfo.pipelineStage ?? null,
          status: taskInfo.status ?? payload?.status,
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    if (!socket) return undefined;

    socket.on('subtask_update', handleSubtaskUpdate);
    socket.on('task_status_update', handleTaskStatusUpdate);

    return () => {
      socket.off('subtask_update', handleSubtaskUpdate);
      socket.off('task_status_update', handleTaskStatusUpdate);
    };
  }, [socket, handleSubtaskUpdate, handleTaskStatusUpdate]);
}
